import { useApolloClient } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import Edit from "@mui/icons-material/Edit";
import Preview from "@mui/icons-material/Preview";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { xsHeight, mdHeight } from "components/AppBar";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import { IssueMessageCell } from "components/DataGrid";
import {
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
  DocumentDialog,
  EditMode,
  FormState,
} from "components/Document";
import { FullScreenDocumentPreview } from "components/Document/shared/FullScreenDocumentPreview";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { SectionComplianceIssuesButton } from "components/SectionComplianceIssuesButton";
import { tabsOffset } from "components/TabLinks";
import { hmbpSectionFromDocumentType } from "encamp-shared/src/hmbp";
import { gql } from "generated-graphql";
import { DocumentType, FacilityDocumentsSearchQuery, Issue } from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useDocumentAI } from "hooks/useDocumentAI";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useDocumentReview } from "providers/documentReview";
import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { documentTypeToLabel } from "util/constants";
import { prettyPrintDateMed } from "util/dates";

import { HmbpDocumentForm } from "../../Chemicals/Report/StateInformation/hmbpComponents/HmbpDocumentForm";
import { useHmbpFeature } from "../../Chemicals/Report/useHmbp";
import { GET_FACILITY, useFacility, useFacilityDocumentIssues } from "../useFacility";

export const FACILITY_DOCUMENTS_SECTION_ID = "facility-documents";
export const FACILITY_DOCUMENTS_SECTION_ICON = "📑";

const FACILITY_DOCUMENTS_SEARCH = gql(`
  query FacilityDocumentsSearch($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    documents(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        title
        fileExtension
        documentType
        createdAt
        authoredAt
        description
        storageLink
        sizeMegabytes
        facilityDocument {
          id
          currentSubmissionDocument
        }
      }
      count
    }
  }
`);

type DocumentRow = FacilityDocumentsSearchQuery["documents"]["items"][number];
type SyntheticIssueRow = { __synthetic: true; id: string; issue: Issue };
type GridRow = DocumentRow | SyntheticIssueRow;

const isSyntheticIssueRow = (row: GridRow): row is SyntheticIssueRow =>
  (row as SyntheticIssueRow).__synthetic === true;

export const FacilityDocumentsSection: React.FC = () => {
  const theme = useTheme();
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();
  const sectionIssues = useFacilityDocumentIssues();
  const { data: facilityData } = useFacility();
  const apolloClient = useApolloClient();
  const alerts = useAlerts();
  const hasDocumentAI = useDocumentAI();
  const { documentsToReview, setDocumentsToReview, clearDocumentsToReview } = useDocumentReview();
  const refetchQueries = useMemo(() => [FACILITY_DOCUMENTS_SEARCH, GET_FACILITY], []);
  const { fetchAndHandleDownload } = useDocumentDownload();
  const [getDownloadLink] = useDocumentDownloadLink();
  const [deleteDocumentMutation, { loading: deletingDocument }] = useDeleteDocumentMutation({
    refetchQueries,
  });
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set(),
  });
  const [docsCount, setDocsCount] = useState<number | undefined>(undefined);
  const [previewState, setPreviewState] = useState<{
    open: boolean;
    fileUrl: string;
    fileExtension: string;
    title: string;
  }>({ open: false, fileUrl: "", fileExtension: "", title: "" });
  const [addEditDialogState, setAddEditDialogState] = useState<AddEditDocumentDialogState>({
    ...defaultAddEditDocumentDialogState,
  });
  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    documentId: string;
    documentTitle: string;
  }>({ open: false, documentId: "", documentTitle: "" });

  const facility = facilityData?.facility;
  const facilityForDialog = useMemo(
    () => (facility ? [{ id: facility.id, name: facility.name }] : undefined),
    [facility]
  );

  const defaultSearch = `tenantId:${tenantId} facilityId:${facilityId} excludeRejectedSubmissionDocuments:true`;
  const [currentSearch, setCurrentSearch] = useState<string>(defaultSearch);
  const isFiltering = currentSearch !== defaultSearch;

  // Mirror the old Facility/Documents.tsx flow: a single narrow list of issues
  // drives BOTH the synthetic rows and the dialog's facilityDocumentIssues prop,
  // so hasMatchingIssue in the dialog always lines up with what rendered the row
  // and currentSubmissionDocument gets flipped on upload.
  const facilityDocumentIssues = useMemo(
    () =>
      facilityData?.facility?.issues.filter(
        (issue) => issue.modelName === "Facility" && issue.key === "facilityDocuments"
      ) ?? [],
    [facilityData?.facility?.issues]
  );

  const syntheticIssueRows: SyntheticIssueRow[] = useMemo(
    () =>
      facilityDocumentIssues.map((issue, index) => ({
        __synthetic: true,
        id: `synthetic-issue-${index}`,
        issue,
      })),
    [facilityDocumentIssues]
  );

  const openAddDialog = useCallback(
    () =>
      setAddEditDialogState((state: AddEditDocumentDialogState) => ({
        ...state,
        open: true,
        mode: EditMode.ADD,
        facilities: facilityForDialog,
      })),
    [facilityForDialog]
  );

  const openAddDialogForIssue = useCallback(
    (documentType?: DocumentType) =>
      setAddEditDialogState((state: AddEditDocumentDialogState) => ({
        ...state,
        open: true,
        mode: EditMode.ADD_OR_PICK,
        documentType,
        currentSubmissionDocument: true,
        isAddingFromIssue: true,
        facilities: facilityForDialog,
      })),
    [facilityForDialog]
  );

  const openEditDialog = useCallback(
    (row: DocumentRow) => {
      if (hasDocumentAI) {
        setDocumentsToReview([row.id]);
        return;
      }
      setAddEditDialogState((state: AddEditDocumentDialogState) => ({
        ...state,
        ...row,
        open: true,
        mode: EditMode.EDIT,
        step: FormState.FORM,
        documentId: row.id,
        currentSubmissionDocument: row.facilityDocument?.currentSubmissionDocument ?? false,
        facilities: facilityForDialog,
      }));
    },
    [hasDocumentAI, setDocumentsToReview, facilityForDialog]
  );

  const isHmbp = useHmbpFeature(facility?.state ?? "");
  const hmbpSection = addEditDialogState.documentType
    ? hmbpSectionFromDocumentType[addEditDialogState.documentType]
    : undefined;
  const shouldShowHmbpForm =
    isHmbp && !!hmbpSection && !!addEditDialogState.isAddingFromIssue && !!tenantId;

  const handleView = useCallback(
    async (row: DocumentRow) => {
      const { data } = await getDownloadLink({ variables: { id: row.id } });
      if (data?.getDocumentDownloadLink) {
        setPreviewState({
          open: true,
          fileUrl: data.getDocumentDownloadLink,
          fileExtension: row.fileExtension ?? "",
          title: row.title,
        });
      }
    },
    [getDownloadLink]
  );

  const columns: GridColDef<GridRow>[] = useMemo(
    () => [
      {
        field: "title",
        headerName: "Name",
        flex: 1.5,
        colSpan: (_, row: GridRow) => (isSyntheticIssueRow(row) ? 4 : undefined),
        renderCell: ({ row }) => {
          if (isSyntheticIssueRow(row)) {
            return <IssueMessageCell issue={row.issue} />;
          }
          return <div title={row.title}>{row.title}</div>;
        },
      },
      {
        field: "fileExtension",
        headerName: "Format",
        flex: 0.5,
      },
      {
        field: "documentType",
        headerName: "Document Type",
        flex: 0.75,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(DocumentType),
        enumPresentationFunction: (enumValue: string) =>
          documentTypeToLabel(enumValue as DocumentType),
        valueGetter: (_, row: GridRow) => {
          if (isSyntheticIssueRow(row)) return "";
          return row.documentType ? documentTypeToLabel(row.documentType) : "";
        },
      },
      {
        field: "createdAt",
        headerName: "Uploaded",
        flex: 0.5,
        valueGetter: (_, row: GridRow) => {
          if (isSyntheticIssueRow(row)) return "";
          return row.createdAt ? prettyPrintDateMed(row.createdAt) : "";
        },
      },
      {
        field: "actions",
        headerName: "",
        flex: 0.4,
        minWidth: 200,
        sortable: false,
        filterable: false,
        align: "right",
        renderCell: ({ row }) => {
          if (isSyntheticIssueRow(row)) {
            const documentType = row.issue.metadata?.documentType as DocumentType | undefined;
            return (
              <Button
                size="small"
                color="primary"
                startIcon={<Add fontSize="small" />}
                onClick={(e) => {
                  e.stopPropagation();
                  openAddDialogForIssue(documentType);
                }}
              >
                Add Document
              </Button>
            );
          }
          return (
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  sx={{ color: "action.active" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditDialog(row);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  sx={{ color: "action.active" }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    await fetchAndHandleDownload({ documentId: row.id });
                  }}
                >
                  <Download fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="View">
                <IconButton
                  size="small"
                  sx={{ color: "action.active" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleView(row);
                  }}
                >
                  <Preview fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  sx={{ color: "action.active" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteDialogState({
                      open: true,
                      documentId: row.id,
                      documentTitle: row.title,
                    });
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [fetchAndHandleDownload, handleView, openAddDialogForIssue, openEditDialog]
  );

  return (
    <Box
      id={FACILITY_DOCUMENTS_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h6">{FACILITY_DOCUMENTS_SECTION_ICON} Facility Documents</Typography>
        <SectionComplianceIssuesButton
          issues={sectionIssues}
          sectionHeader={`${FACILITY_DOCUMENTS_SECTION_ICON} Facility Documents`}
        />
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Showing all current facility documents. To see specific requirements for your program area,
        view{" "}
        <MuiLink component={Link} to={`/o/${tenantId}/chemicals/documents/${facilityId}`}>
          Chemicals Documents
        </MuiLink>
        .
      </Typography>

      {docsCount === 0 && facilityDocumentIssues.length === 0 && (
        <Alert severity="info" sx={{ mb: 2, fontSize: "0.75rem" }}>
          Your state does not require this facility to provide any additional documentation, however
          you can voluntarily add documentation like emergency response plans or site plans here.
        </Alert>
      )}

      <OmnisearchDataGrid
        withPadding={false}
        persistenceKey={`facility-documents-v2-${facilityId}`}
        columns={columns}
        dataQuery={FACILITY_DOCUMENTS_SEARCH}
        commandButtons={[
          ...(hasDocumentAI && selectedRows.ids.size > 0
            ? [
                <CommandButton
                  key="edit-details-button"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => {
                    setDocumentsToReview(Array.from(selectedRows.ids) as string[]);
                  }}
                >
                  Edit Details ({selectedRows.ids.size})
                </CommandButton>,
              ]
            : []),
          <CommandButton
            key="add-document-button"
            variant="contained"
            startIcon={<Add />}
            onClick={() => openAddDialog()}
          >
            Add Document
          </CommandButton>,
        ]}
        defaultSearch={defaultSearch}
        onSearchChanged={setCurrentSearch}
        onDataStateChange={(data) => setDocsCount(data?.documents.count ?? 0)}
        getItems={(data) =>
          isFiltering ? data.documents.items : [...data.documents.items, ...syntheticIssueRows]
        }
        getCount={(data) => data.documents.count ?? 0}
        initialSortModel={[{ field: "createdAt", sort: "desc" }]}
        noDataMessage="No documents for this facility."
        excludeFilterColumns={["createdAt", "fileExtension", "documentType"]}
        additionalFilterColumns={[
          {
            key: "documentType",
            header: "Document Type",
            filterKeyType: "enumAutocomplete",
            enumValues: Object.values(DocumentType),
            enumPresentationFunction: (enumValue) => documentTypeToLabel(enumValue as DocumentType),
          },
          {
            header: "Tag",
            key: "tags",
          },
        ]}
        rowHeight="auto"
        checkboxSelection={hasDocumentAI}
        onSelectedIdsChanged={
          hasDocumentAI
            ? (ids) => setSelectedRows({ type: "include", ids: new Set(ids) })
            : undefined
        }
        selectedIds={hasDocumentAI ? Array.from(selectedRows.ids) : undefined}
        isRowSelectable={(params) => !isSyntheticIssueRow(params.row)}
        disableRowSelectionOnClick
        onRowClick={(params) => {
          if (isSyntheticIssueRow(params.row)) {
            const documentType = params.row.issue.metadata?.documentType as
              | DocumentType
              | undefined;
            openAddDialogForIssue(documentType);
          } else {
            openEditDialog(params.row);
          }
        }}
        showManageColumns={false}
        gridSx={{
          "& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within":
            { outline: "none" },
          "& .MuiDataGrid-columnSeparator": { display: "none" },
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
          },
          '& .MuiDataGrid-row[data-id^="synthetic-issue-"] .MuiDataGrid-cellCheckbox': {
            visibility: "hidden",
          },
        }}
      />

      <FullScreenDocumentPreview
        open={previewState.open}
        onClose={() => setPreviewState((prev) => ({ ...prev, open: false }))}
        fileUrl={previewState.fileUrl}
        fileExtension={previewState.fileExtension}
        title={previewState.title}
      />

      {shouldShowHmbpForm && hmbpSection ? (
        <HmbpDocumentForm
          section={hmbpSection}
          handleClose={() => setAddEditDialogState(defaultAddEditDocumentDialogState)}
        />
      ) : (
        tenantId && (
          <DocumentDialog
            tenantId={tenantId}
            reviewDialogProps={{
              open: documentsToReview.length > 0,
              onClose: () => {
                clearDocumentsToReview();
                setSelectedRows({ type: "include", ids: new Set() });
              },
              documentIds: documentsToReview,
              onComplete: () => {
                apolloClient.refetchQueries({ include: refetchQueries });
              },
            }}
            addEditDialogProps={{
              dialogState: addEditDialogState,
              setDialogState: setAddEditDialogState,
              state: facility?.state,
              refetchQueries: refetchQueries,
              facilityDocumentIssues: facilityDocumentIssues,
              onSubmit: () => {
                apolloClient.refetchQueries({ include: refetchQueries });
              },
            }}
          />
        )
      )}

      {deleteDialogState.open && (
        <ConfirmDialog
          open={deleteDialogState.open}
          header="Are you sure you want to delete this document?"
          info="This action is permanent. This document may be associated with other facilities, reports and chemicals."
          resources={[{ resourceType: "Document", resourceName: deleteDialogState.documentTitle }]}
          confirmText="Yes, Delete"
          loading={deletingDocument}
          onClose={() => setDeleteDialogState({ open: false, documentId: "", documentTitle: "" })}
          onConfirm={async () => {
            await deleteDocumentMutation({
              variables: { id: deleteDialogState.documentId },
            });
            setDeleteDialogState({ open: false, documentId: "", documentTitle: "" });
            alerts.success("Document successfully deleted");
          }}
        />
      )}
    </Box>
  );
};
